/* eslint-disable import/no-extraneous-dependencies */
import { AecomCoreModule, LayoutService } from "@aecom/core";
import { CommonModule } from "@angular/common";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { APP_INITIALIZER, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ApiModule, BASE_PATH } from "@api";
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalRedirectComponent,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { BBJFileUrlProvider, AuthInterceptor, AuthService, AzureBlobService, BBJCoreModule, BBJFileListService, BBJSelectUserService } from "@bbj/components";
import CreateComponent from "@content/corr/create/create.component";
import IssuedComponent from "@content/corr/issued/issued.component";
import ListComponent from "@content/corr/list/list.component";
import ViewComponent from "@content/corr/view/view.component";
import CreateStepResolver from "@resolvers/createStep.resolver";
import ListResolver from "@resolvers/list.resolver";
import ViewResolver from "@resolvers/view.resolver";
import LocalContractUserService from "@services/local-contractUser.service";
import LocalUserService from "@services/local-user.service";
import NavService from "@services/nav.service";
import CorrDetailComponent from "@shared/corrDetail/corrDetail.component";
import CorrHeaderComponent from "@shared/corrHeader/corrHeader.component";
import CorrProcessingComponent from "@shared/corrProcessing/corrProcessing.component";
import LeftPanelComponent from "@shared/leftPanel/leftPanel.component";
import ManagerResponseDetailComponent from "@shared/managerResponseDetail/managerResponseDetail.component";
import ResponseDetailComponent from "@shared/responseDetail/responseDetail.component";
import { NgxSpinnerModule } from "ngx-spinner";
import environment from "src/environments/environment";
import { PreviousRouteService } from "@services/previousRoute.service";
import ErrorComponent from "@content/error/error.component";
import { OAuthSettings } from "../oauth";
import AppRoutingModule from "./app-routing.module";
import AppComponent from "./app.component";
import BaseComponent from "./content/base/base.component";
import SignoutComponent from "./content/signout/signout.component";
import { PendingChangesGuard } from "./guards/pending-changes.guard";
import ErrorResolve from "./resolvers/error-resolver";
import LoginUserResolve from "./resolvers/login-user-resolver";
import LocalContractService from "./services/local-contract.service";
import LocalStorageService from "./services/local-storage.service";
import { StorageKey } from "./shared/localstorage";
import { WithdrawModalComponent } from "@shared/withdraw-modal/withdraw-modal.component";
import BaseFunctionService from "@services/baseFunction.service";
import LocalTaskService from "@services/local-task.service";
import CorrespondenceFileUrlProvider from "./services/fileUrl.service";

export function tokenGetter(): string {
  return localStorage.getItem(StorageKey.TOKEN);
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: OAuthSettings.appId,
      authority: OAuthSettings.authority,
      redirectUri: environment.url.redirectUri,
      postLogoutRedirectUri: environment.url.logoutURL,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      },
      iframeBridgeTimeout: 10000,
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: OAuthSettings.protectedResourceMap,
    // unprotectedResources: OAuthSettings.unprotectedResources,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: OAuthSettings.scopes,
    },
    loginFailedRoute: environment.url.redirectUri,
  };
}


export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

@NgModule({
  declarations: [
    AppComponent,
    BaseComponent,
    ErrorComponent,
    CorrHeaderComponent,
    CorrDetailComponent,
    CorrProcessingComponent,
    ResponseDetailComponent,
    ManagerResponseDetailComponent,
    LeftPanelComponent,
    CreateComponent,
    IssuedComponent,
    ListComponent,
    ViewComponent,
    SignoutComponent,
    WithdrawModalComponent
  ],
  imports: [
    AecomCoreModule,
    BBJCoreModule,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    NgxSpinnerModule,
    MsalModule,
    BrowserAnimationsModule,
    ApiModule,
  ],
  providers: [
    // Fresh SAS per click; without this the library falls back to the stored, expiring URLs.
    { provide: BBJFileUrlProvider, useExisting: CorrespondenceFileUrlProvider },
    PendingChangesGuard,
    BaseFunctionService,
    BBJFileListService,
    BBJSelectUserService,
    LocalStorageService,
    LocalContractService,
    LocalContractUserService,
    LocalUserService,
    LocalTaskService,
    PreviousRouteService,
    NavService,
    AuthService,
    LayoutService,
    AzureBlobService,
    LoginUserResolve,
    ErrorResolve,
    ListResolver,
    ViewResolver,
    CreateStepResolver,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: BASE_PATH, useValue: environment.baseWebAPI },
    MsalBroadcastService,
    MsalService,

    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true,
    },
    MsalGuard,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
